import { pluralize } from '@/utils/string.utils'
import { ErrorType } from '@/types/errors.types'

/**
 * Error tag used in authentication code
 * prepended to error messages for easy identification
 */
export const AUTH_ERROR_TAG = 'AUTH_ERROR: '
/**
 * Error messages used in authentication
 */
export const AUTH_ERRORS = {
  noSessionCookie: 'No session cookie found',
  unableToFetchUser: 'Unable to fetch user',
  unableToGetUserInfo: 'Unable to get user info',
  unableToFormatError: 'Unable to format error',
}

export const GENERAL_ERRORS = {
  addToCart: 'Oops! Something went wrong. Please reload and try again',
  loadTickets: 'Oops! We couldn\'t load tickets. Please reload and try again.',
  noTicketsForDate: (date: string) => `Sorry, there are no tickets available for ${date}.`,
  noTickets: 'We don\'t have any time slots available for the selected date. Please choose another date to continue.',
  invalidPostalCode: (stateName: string) => (
    `The provided zip is outside the immediate vicinity of ${stateName}.`
  ),
  noResults: 'No Results',
  noActiveEvents: 'No events in this corner of the multiverse. Ask again later.',
  missingRequiredFields: 'Missing required fields',
  stripeNotInitialized: 'Stripe elements not initialized',
  stripeNotInitializedConfirmPayment: 'Stripe not initialized: confirm payment',
  modifyCartNoSellerID: 'Seller ID is required to modify the cart.',
}

export const GENERAL_ALERTS = {
  eventSoldOut: 'Sold Out',
  noTicketsAvailable: 'No tickets available',
  ticketsUnavailable: 'Tickets Unavailable',
  lowTicketWarning: 'Low Ticket Warning!',
  unsavedChanges: 'You have unsaved changes - are you sure you wish to leave this page?',
  outOfState: 'We noticed you have local tickets in your cart. <span class="bold">Please note IDs are checked at the door</span>.',
  aboveTerms: (variant: 'exhibition' | 'events', city: string, venue?: string): string => {
    switch (variant) {
    case 'exhibition':
      return `You are purchasing tickets valid for <span class="bold">${venue}</span> in <span class="bold">${city}</span>.`
    case 'events':
    default:
      return `You are purchasing tickets valid in <span class="bold">${city}</span>.`
    }
  },
  orderProcessing: 'Please do not navigate away from or refresh this page.',
  perkUnavailable: (membershipRole?: string) => `${membershipRole} Perks can only be used for timed tickets.`,
  remainingTickets: (remaining: string | number) => `${remaining} remaining tickets`,
  pendingDiscount: 'Your discount will be applied once you have added tickets to your cart.',
  benefitsExpire: (isValidTo: string) => `<strong>Expires:</strong> ${isValidTo}`,
  maxTickets: (maxPerCategory: number, title: string) => `You may only purchase ${maxPerCategory} ${title} tickets`,
  maxTicketsPerOrder: (max: number) => `Maximum of ${max} tickets per order`,
  proofOfResidencyRequired: 'Valid photo ID required at entry.',
  onSale: 'On Sale',
}

/**
 * Messages used in ErrorNotifications.tsx
 * and in ModalProvider.tsx
 */
export const ERROR_NOTIFICATIONS = {
  toastTitle: 'Oops!',
  isReloading: (message: string, time: number) => `${message} Reloading in ${time} ${pluralize(time, 'second')}`,
  reloadedMessage: 'Something went wrong. Please clear your browser cookies.',
  genericMessage: 'Something went wrong!',
  timeslotElapsed: 'The selected time is no longer available.',
  dateElapsed: 'The selected day is no longer available.',
  sessionExpired:
    'It looks like you were signed out for some reason. Please <a href="/api/auth/login">login</a> again to continue.',
  paymentFallback: 'Error processing payment',
}

/**
 * Form error messages
 */
export const FORM_ERRORS = {
  couponCode: 'Coupon code contains invalid characters',
  email: 'Please enter a valid email address',
  emailConfirm: 'Email address must match',
  firstName: 'First name contains invalid characters',
  lastName: 'Last name contains invalid characters',
  phone: 'Please enter a valid phone number',
  required: 'Required',
  zip: 'The code appears to be invalid, please try again.',
  zipMinLength: 'Must be at least 4 characters',
  zipMaxLength: 'Can\'t exceed 10 characters',
  giftEmail: 'The giftee\'s email address cant match the giver.',
}

export const IGNORED_USER_ERRORS = [
  'is already registered and cannot be re-created',
]

export const ERROR_MODAL_MESSAGES = {
  errorTypeNotFound: 'ErrorType not found',
  connection: 'We\'re sorry, something has gone wrong. Please refresh and try again.',
  loadingTickets: 'Error loading ticket types. Please refresh and try again.',
  loadingAddOns: 'Error loading add-ons. Please refresh and try again.',
  loadingCalendar: 'Error loading calendar dates. Please refresh and try again.',
  loadingTimeslots: 'Error loading time slots. Please refresh and try again.',
  noAvailableDates: 'We\'re sorry, there are no available tickets at this time.',
  addTicketsToCart: 'Error adding tickets to cart. Please refresh and try again.',
  addAddOnsToCart: 'Error adding add-ons to cart. Please refresh and try again.',
  processingCard: 'There was an error processing your card.',
  processingCardSubtitle:
    'Please verify the information you entered is correct. If problems persist please contact customer support.',
  contactForm: 'Something went wrong. Please check the form and try again',
  cartInactive: 'Sorry, your cart has expired. You\'ll have to restart your purchase.',
  redeemPerk: 'Something went wrong redeeming your perk, please try again',
  login: 'We\'ve encountered an error',
  loginSubtitle: 'Please try logging in again',
  sessionExpired: 'Oops! Your session has expired.',
  capacityExceeded: 'Sorry, there aren\'t enough tickets left for that selection. Please reduce the quantity and try again.',
  notFound: 'Sorry, we couldn\'t find what you were looking for.',
  cartNotFound: 'Sorry, we couldn\'t find your cart. It may have expired.',
  eventNotFound: 'Sorry, we couldn\'t find that event.',
  sessionNotFound: 'Sorry, we couldn\'t find that session',
}

const ErrorMessages = ERROR_MODAL_MESSAGES

/**
 * Message interface
 * @param {string} title - title of the message
 * @param {React.ReactNode | string} jsxTitle - ReactNode title of the message
 * @param {React.ReactNode | string} body - body of the message
 */
type Message = {
  title: string
  jsxTitle?: React.ReactNode | string
  body?: React.ReactNode | string
}

export const ErrorsConfig = new Map<ErrorType, Message>([
  // Session Expired
  [
    ErrorType.SessionExpired,
    { title: ErrorMessages.sessionExpired },
  ],
  // Internal error
  [
    ErrorType.ErrorTypeNotFound,
    { title: ErrorMessages.errorTypeNotFound },
  ],

  // Error fetching ticket socket token, etc
  [
    ErrorType.Connection,
    { title: ErrorMessages.connection },
  ],

  // Loading Errors
  [
    ErrorType.LoadingTickets,
    { title: ErrorMessages.loadingTickets },
  ],
  [
    ErrorType.LoadingAddOns,
    { title: ErrorMessages.loadingAddOns },
  ],
  [
    ErrorType.LoadingCalendar,
    { title: ErrorMessages.loadingCalendar },
  ],
  [
    ErrorType.NoAvailableDates,
    { title: ErrorMessages.noAvailableDates },
  ],
  [
    ErrorType.LoadingTimeslots,
    { title: ErrorMessages.loadingTimeslots },
  ],

  // Add to Cart Errors
  [
    ErrorType.AddTicketsToCart,
    { title: ErrorMessages.addTicketsToCart },
  ],
  [
    ErrorType.AddAddOnsToCart,
    { title: ErrorMessages.addAddOnsToCart },
  ],
  [
    ErrorType.CartInactive,
    { title: ErrorMessages.cartInactive },
  ],
  [
    ErrorType.CapacityExceeded,
    { title: ErrorMessages.capacityExceeded },
  ],

  [
    ErrorType.ContactForm,
    { title: ErrorMessages.contactForm },
  ],
  [
    ErrorType.RedeemPerk,
    { title: ErrorMessages.redeemPerk },
  ],
  [
    ErrorType.NotFound,
    { title: ErrorMessages.notFound },
  ],
  [
    ErrorType.CartNotFound,
    { title: ErrorMessages.cartNotFound },
  ],
  [
    ErrorType.EventNotFound,
    { title: ErrorMessages.eventNotFound },
  ],
  [
    ErrorType.SessionNotFound,
    { title: ErrorMessages.sessionNotFound },
  ],

  // Payment Errors
  [
    ErrorType.ProcessingCard,
    {
      title: ErrorMessages.processingCard,
      jsxTitle: (
        <>
          <strong>
            { ErrorMessages.processingCard }
            {' '}
          </strong>
          <br />
          { ErrorMessages.processingCardSubtitle }
        </>
      ),
    },
  ],

  [
    ErrorType.Login,
    {
      title: ErrorMessages.login,
      jsxTitle: (
        <>
          <strong>
            { ErrorMessages.login }
            {' '}
          </strong>
          <br />
          { ErrorMessages.loginSubtitle }
        </>
      ),
    },
  ],
])
